import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';

import Collection from '../containers/Collection/Collection';
import CustomCollection from '../containers/CustomCollection/CustomCollection';
import CustomCollectionSliderTemplate from '../containers/CustomCollectionSliderTemplate/CustomCollectionSliderTemplate';
import Loading from '../components/Loading/Loading';

import useFetch from '../hooks/useFetch';
import { queryCollectionProductsByHandle } from '../Queries';

// base collection query

var _ref = React.createElement(
  'div',
  { className: 'collection-page wrapper loading-wrapper' },
  React.createElement(Loading, null)
);

var CollectionQuery = function CollectionQuery(props) {
  var collectionProductCount = window.Dormify.collectionProductCount;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      fetchCount = _useState2[0],
      setFetchCount = _useState2[1];

  var hasMoreProductData = useRef(false);
  var firstFetchAmount = props.customCollectionId ? 250 : 24;

  var _useQuery = useQuery(queryCollectionProductsByHandle, { variables: { handle: props.handle, amount: firstFetchAmount } }),
      loading = _useQuery.loading,
      error = _useQuery.error,
      data = _useQuery.data,
      fetchMore = _useQuery.fetchMore,
      refetch = _useQuery.refetch;

  if (loading && hasMoreProductData.current || !data.collectionByHandle) {
    return _ref;
  }
  if (error) throw new Error(error);

  var checkIfMoreProducts = function checkIfMoreProducts(productCount) {
    if (productCount > firstFetchAmount && // if productCount is more than firstFetchAmount, its using cached data
    !hasMoreProductData.current && // and its not going to fetch more data
    productCount < collectionProductCount // and the count does not equal collection product count,
    ) {
        refetch({ fetchPolicy: 'network-only' }); // We need to fetch again!
        hasMoreProductData.current = true;
        setFetchCount(0);
      } else if (
    // first fetch = 24 then subsequent fetches are 75 each
    // if fetchCount and product count don't match, fetch more
    fetchCount * 24 + 24 === productCount && productCount < collectionProductCount) {
      hasMoreProductData.current = true;
      setFetchCount(fetchCount + 1);
      return true;
    } else {
      return false;
    }
  };

  var productCount = data.collectionByHandle.products.edges.length;

  // see if we need to get more products from graphql
  if (checkIfMoreProducts(productCount)) {
    var cursor = data.collectionByHandle.products.edges[data.collectionByHandle.products.edges.length - 1].cursor;

    fetchMore({
      query: queryCollectionProductsByHandle,
      variables: {
        cursor: cursor,
        handle: data.collectionByHandle.handle,
        amount: 24
      },
      updateQuery: function updateQuery(previousResult, _ref2) {
        var fetchMoreResult = _ref2.fetchMoreResult;

        var previousProducts = previousResult.collectionByHandle.products;
        var newProducts = fetchMoreResult.collectionByHandle.products;
        return newProducts.edges.length ? {
          collectionByHandle: Object.assign({}, previousResult.collectionByHandle, {
            products: {
              pageInfo: {
                hasNextPage: newProducts.pageInfo.hasNextPage,
                __typename: newProducts.pageInfo.__typename
              },
              __typename: newProducts.__typename,
              edges: [].concat(_toConsumableArray(previousProducts.edges), _toConsumableArray(newProducts.edges))
            }
          })
        } : previousResult;
      }
    });
  } else if (productCount === collectionProductCount) {
    hasMoreProductData.current = false;
  }

  return props.customCollectionId || props.customSliderCollectionId ? React.createElement(CustomCollectionLoader, Object.assign({}, props, { collection: data.collectionByHandle })) : React.createElement(CollectionLoader, Object.assign({}, props, {
    collection: data.collectionByHandle,
    hasMoreProductData: hasMoreProductData,
    collectionProductCount: collectionProductCount
  }));
};

// Collection specific data (filterdata + Collection component)
var CollectionLoader = function CollectionLoader(props) {
  var filterData = useFetch('/collections/' + props.handle + '?view=filterdata');
  return React.createElement(Collection, {
    title: props.collection.title,
    handle: props.handle,
    products: props.collection.products.edges,
    filterData: filterData,
    addVariantToCart: props.addVariantToCart,
    checkout: props.checkout,
    queryParams: props.location.search,
    location: props.location,
    isCartOpen: props.isCartOpen,
    isTouchDevice: props.isTouchDevice,
    wishlistProducts: props.wishlistProducts,
    hasMoreProductData: props.hasMoreProductData.current,
    storeFrontProductCount: props.collection.products.edges.length,
    collectionProductCount: props.collectionProductCount,
    mainMenu: props.mainMenu,
    toggleAccountModal: props.toggleAccountModal,
    lowStockThreshold: props.lowStockThreshold,
    dormifyXYouPage: props.dormifyXYouPage
  });
};

// Custom Collection specific data (gsData + customCollectionImages + CustomCollection component)
var CustomCollectionLoader = function CustomCollectionLoader(props) {
  var collectionId = props.customCollectionId || props.customSliderCollectionId;
  var gsData = useFetch('https://gs-collections.herokuapp.com/collections/' + collectionId + '/json?keys=1');
  var customCollectionImages = useFetch('/collections/' + props.handle + '?view=metafieldimages');

  if (props.customSliderCollectionId) return React.createElement(CustomCollectionSliderTemplate, {
    title: props.collection.title,
    descriptionHtml: props.collection.descriptionHtml,
    handle: props.handle,
    products: props.collection.products.edges,
    addVariantToCart: props.addVariantToCart,
    checkout: props.checkout,
    collectionImage: props.collection.image.originalSrc,
    customCollectionImages: customCollectionImages,
    roomPanoCollectionId: props.roomPanoCollectionId,
    isCartOpen: props.isCartOpen,
    gsData: gsData,
    handleCartOpen: props.handleCartOpen,
    isTouchDevice: props.isTouchDevice,
    wishlistProducts: props.wishlistProducts,
    toggleAccountModal: props.toggleAccountModal,
    nonPano: props.nonPano,
    customCollectionData: props.customCollectionData
  });

  return React.createElement(CustomCollection, {
    title: props.collection.title,
    descriptionHtml: props.collection.descriptionHtml,
    handle: props.handle,
    products: props.collection.products.edges,
    addVariantToCart: props.addVariantToCart,
    checkout: props.checkout,
    gsData: gsData,
    collectionImage: props.collection.image.originalSrc,
    customCollectionImages: customCollectionImages,
    roomPanoCollectionId: props.roomPanoCollectionId,
    isCartOpen: props.isCartOpen,
    handleCartOpen: props.handleCartOpen,
    isTouchDevice: props.isTouchDevice,
    wishlistProducts: props.wishlistProducts,
    toggleAccountModal: props.toggleAccountModal,
    nonPano: props.nonPano,
    customCollectionData: props.customCollectionData
  });
};

CollectionQuery.propTypes = {
  addVariantToCart: PropTypes.func.isRequired,
  checkout: PropTypes.object.isRequired,
  title: PropTypes.string,
  handle: PropTypes.string,
  location: PropTypes.object,
  handleCartOpen: PropTypes.func,
  wishlistProducts: PropTypes.array,
  customCollectionId: PropTypes.string,
  roomPanoCollectionId: PropTypes.string,
  isCartOpen: PropTypes.bool,
  isTouchDevice: PropTypes.bool,
  hasMoreProductData: PropTypes.object,
  collectionProductCount: PropTypes.number,
  lowStockThreshold: PropTypes.string,
  dormifyXYouPage: PropTypes.bool
};

CollectionLoader.propTypes = {
  addVariantToCart: PropTypes.func.isRequired,
  checkout: PropTypes.object.isRequired,
  collection: PropTypes.object,
  title: PropTypes.string,
  handle: PropTypes.string,
  handleCartOpen: PropTypes.func,
  location: PropTypes.object,
  wishlistProducts: PropTypes.array,
  isCartOpen: PropTypes.bool,
  isTouchDevice: PropTypes.bool,
  hasMoreProductData: PropTypes.object,
  collectionProductCount: PropTypes.number,
  mainMenu: PropTypes.array,
  toggleAccountModal: PropTypes.func.isRequired,
  lowStockThreshold: PropTypes.string,
  dormifyXYouPage: PropTypes.bool
};

CustomCollectionLoader.propTypes = {
  addVariantToCart: PropTypes.func.isRequired,
  checkout: PropTypes.object.isRequired,
  collection: PropTypes.object,
  title: PropTypes.string,
  handle: PropTypes.string,
  handleCartOpen: PropTypes.func,
  wishlistProducts: PropTypes.array,
  customCollectionId: PropTypes.string,
  roomPanoCollectionId: PropTypes.string,
  isCartOpen: PropTypes.bool,
  isTouchDevice: PropTypes.bool,
  toggleAccountModal: PropTypes.func.isRequired
};

export default CollectionQuery;